import { EXTEND, IF, MAKE_ID, NO, UITextView, UIView, YES } from "uicore-ts"
import { RHProcurementPart, RHRequiredDocument } from "../../../models/ProcurementModels"
import { RHButton } from "../Custom components/RH/RHButton"
import { RHCheckbox } from "../Custom components/RH/RHCheckbox"
import { RHColor } from "../Custom components/RH/RHColor"
import { RHTextField } from "../Custom components/RH/RHTextField"
import type { RHValidationIssue } from "../Custom components/RH/RHValidationIssuesDialogView"
import { RHButtons, RHColors, RHForms, RHRadius, RHSpacing } from "../Custom components/RH/Utils_slop/RHDesignTokens"
import { RHProcurementPartDisplay } from "../Custom components/RH/Utils_slop/RHProcurementPartDisplay"
import {
    RHNewProcurementViewTokens,
    RHProcurementWorkflowTitleTokens,
    RHRequiredDocumentsEditorViewTokens
} from "../Custom components/RH/Utils_slop/RHViewTokens"
import {
    ProcurementTemplateLockButton,
    type ProcurementTemplateLockControlConfiguration
} from "./ProcurementTemplateLockButton"
import { RHRichTextEditorView } from "../Custom components/RH/RHRichTextEditorView"


export type RequiredDocumentsEditorSessionSnapshot = {
    rows: Array<{
        documentID: string
        nameText: string
        descriptionText: string
        descriptionFormat?: RHRequiredDocument["descriptionFormat"]
        checkedPartNumbers: number[]
    }>
}


// ============================================================================
// RequiredDocumentRowView
// A single required document slot: name field, description field, part checkboxes.
// ============================================================================

class RequiredDocumentRowView extends UIView {
    
    readonly documentID: string
    
    readonly nameLabel = new UITextView().addedAsSubviewToView(this).configuredWithObject({
        innerHTML: "Dokumendi nimetus <span style=\"color: " + RHColor.danger.stringValue + "\">*</span>",
        style: {
            fontWeight: RHNewProcurementViewTokens.fieldLabelFontWeight
        }
    })
    
    readonly nameTextField = new RHTextField().addedAsSubviewToView(this).configuredWithObject({
        placeholderText: "Dokumendi nimetus (nt maksuõiend)",
        isRequired: YES
    })
    
    set isNameRequired(isRequired: boolean) {
        this.nameTextField.isRequired = isRequired
        this.nameLabel.innerHTML = "Dokumendi nimetus" + IF(isRequired)(() =>
            ` <span style="color: ${RHColor.danger.stringValue}">*</span>`
        ).ELSE(() => "")
        if (!isRequired) {
            this.nameTextField.resetValidationVisuals()
        }
    }

    readonly descriptionEditor = new RHRichTextEditorView().addedAsSubviewToView(this).configuredWithObject({
        placeholderText: "Kirjeldus (valikuline)"
    })

    readonly partSelectionValidationMessageLabel = new UITextView().configuredWithObject({
        text: "Nõutud dokument ei ole seotud ühegi osaga. Vali osa või eemalda dokument.",
        textColor: RHColor.danger,
        isSingleLine: NO
    })
    
    readonly removeButton = new RHButton().addedAsSubviewToView(this).configuredWithObject({
        hoverText: "Eemalda nõutud dokument",
        titleLabel: {
            text: RHButtons.deleteIconHTML,
            style: {
                fontSize: RHButtons.dialogCloseFontSize,
                lineHeight: RHButtons.dialogCloseLineHeight
            }
        },
        variant: RHButtons.dialogCloseVariant,
        contentPadding: RHButtons.dialogCloseContentPadding,
        style: {
            padding: RHButtons.dialogClosePadding,
            borderRadius: RHButtons.dialogCloseBorderRadius
        }
    })
    
    // One checkbox per part — rebuilt whenever parts change
    _partCheckboxes: RHCheckbox[] = []
    _partCheckboxLabels: UITextView[] = []
    _parts: Pick<RHProcurementPart, "partNumber" | "_id">[] = []
    
    removalWasRequested?: () => void
    rowDidChange?: () => void
    _isDocumentListReadOnly = NO
    _readOnlyPartIds: string[] = []
    _isDocumentReadOnly = NO
    _originalDescription: Pick<RHRequiredDocument, "description" | "descriptionFormat"> = {}
    
    constructor(elementID?: string, documentID = MAKE_ID()) {
        super(elementID)
        this.documentID = documentID;
        
        this.backgroundColor = RHColors.surfaceMuted
        this.setBorder(RHRadius.medium, 1, RHColors.borderMuted)
        
        // noinspection JSConstantReassignment
        this.removeButton.controlEventTargetAccumulator.PrimaryActionTriggered = () => {
            this.removalWasRequested?.()
        }
        this.nameTextField.controlEventTargetAccumulator.TextChange = () => {
            this.rowDidChange?.()
        }
        this.descriptionEditor.editorDidChange = () => {
            this.rowDidChange?.()
        }
        
    }


    set isDocumentListReadOnly(isDocumentListReadOnly: boolean) {
        this._isDocumentListReadOnly = isDocumentListReadOnly
        this._applyReadOnlyState()
    }

    _applyReadOnlyState() {
        this._isDocumentReadOnly = this._isDocumentListReadOnly || this._parts.some(part =>
            this._readOnlyPartIds.includes(part._id?.toString() ?? "") &&
            this.checkedPartNumbers().includes(part.partNumber)
        )
        this.nameTextField.configureWithObject({
            enabled: !this._isDocumentReadOnly,
            textElementView: { viewHTMLElement: { readOnly: this._isDocumentReadOnly } }
        })
        this.descriptionEditor.enabled = !this._isDocumentReadOnly
        this.removeButton.enabled = !this._isDocumentReadOnly
        this._partCheckboxes.forEach((checkbox, index) => {
            checkbox.enabled = !this._isDocumentListReadOnly &&
                !this._readOnlyPartIds.includes(this._parts[index]._id?.toString() ?? "")
        })
    }

    get isDocumentListReadOnly(): boolean {
        return this._isDocumentListReadOnly
    }
    
    get isDocumentReadOnly(): boolean {
        return this._isDocumentReadOnly
    }
    
    get readOnlyPartIds(): string[] {
        return this._readOnlyPartIds
    }
    
    set readOnlyPartIds(readOnlyPartIds: string[]) {
        this._readOnlyPartIds = [...readOnlyPartIds]
        this._applyReadOnlyState()
    }
    
    
    /**
     * Rebuilds the per-part checkboxes to match the current parts list.
     * Preserves checked state by stable part ID when display numbers change.
     */
    updatePartCheckboxesForParts(parts: Pick<RHProcurementPart, "partNumber" | "_id">[]) {
        
        const previouslyCheckedPartIds = new Set<string>()
        this._partCheckboxes.forEach((checkbox, index) => {
            const partId = this._parts[index]?._id?.toString()
            if (checkbox.selected && partId) {
                previouslyCheckedPartIds.add(partId)
            }
        })
        
        // Remove old checkboxes and labels from the view tree
        this._partCheckboxes.forEach(checkbox => checkbox.removeFromSuperview())
        this._partCheckboxLabels.forEach(label => label.removeFromSuperview())
        
        // Build fresh sets — default checked for any part that was checked before
        // (or all checked if this is a fresh row with no prior state)
        const hasNoPreviousParts = this._parts.length === 0
        this._parts = parts.map(part => ({ ...part }))

        this._partCheckboxes = parts.map(part => {
            const checkbox = new RHCheckbox().addedAsSubviewToView(this).configuredWithObject({
                selected: previouslyCheckedPartIds.has(part._id?.toString() ?? "") ||
                    (hasNoPreviousParts && !this._readOnlyPartIds.includes(part._id?.toString() ?? "")),
                enabled: !this._isDocumentListReadOnly,
                controlEventTargetAccumulator: {
                    SelectionChange: () => {
                        this._updatePartSelectionValidationMessage()
                        this.rowDidChange?.()
                    }
                }
            })
            return checkbox
        })
        
        this._partCheckboxLabels = parts.map(part => {
            return new UITextView().addedAsSubviewToView(this).configuredWithObject({
                text: IF(RHProcurementPartDisplay.shouldShowPartIdentityForPartCount(parts.length))(() =>
                    `Osa ${part.partNumber}`
                ).ELSE(() => "Kehtib hankele"),
                style: {
                    // fontSize: "13px",
                    fontSize: "13px",
                    // color: RHColor.textSecondary.stringValue
                    color: RHColors.grayMedium.stringValue
                }
            })
        })
        
        this._applyReadOnlyState()
        this._updatePartSelectionValidationMessage()
    }
    
    
    /**
     * Returns the part numbers for which this document is checked.
     */
    checkedPartNumbers(): number[] {
        return this._parts
            .filter((part, index) => this._partCheckboxes[index]?.selected)
            .map(part => part.partNumber)
    }


    _updatePartSelectionValidationMessage() {
        if (this.checkedPartNumbers().length === 0) {
            this.partSelectionValidationMessageLabel.text = IF(this._parts.length === 1)(() =>
                "Nõutud dokument ei ole hankega seotud. Märgi „Kehtib hankele” või eemalda dokument."
            ).ELSE(() => "Nõutud dokument ei ole seotud ühegi osaga. Vali osa või eemalda dokument.")
            this.partSelectionValidationMessageLabel.addedAsSubviewToView(this)
        }
        else {
            this.partSelectionValidationMessageLabel.removeFromSuperview()
        }
        this.setNeedsLayoutUpToRootView()
    }
    
    
    /**
     * Builds the RHRequiredDocument value from current field state.
     */
    requiredDocument(): RHRequiredDocument {
        const document: RHRequiredDocument = {
            _id: this.documentID,
            name: this.nameTextField.text.trim()
        }
        if (this._isDocumentReadOnly) {
            return { ...document, ...this._originalDescription }
        }
        if (!this.descriptionEditor.isEmpty) {
            document.description = this.descriptionEditor.html
            document.descriptionFormat = "html"
        }
        return document
    }


    loadDescription(description: Pick<RHRequiredDocument, "description" | "descriptionFormat">) {
        // Locked legacy descriptions must retain their original representation on save.
        this._originalDescription = {
            description: description.description,
            descriptionFormat: description.descriptionFormat
        }
        if (description.descriptionFormat === "html") {
            this.descriptionEditor.html = description.description ?? ""
        }
        else {
            this.descriptionEditor.loadPlainText(description.description ?? "")
        }
    }
    
    
    override layoutSubviews() {
        super.layoutSubviews()
        
        const padding = this.core.paddingLength
        const labelHeight = padding * 2
        const fieldHeight = RHForms.fieldSingleLineFieldHeight
        const bounds = this.contentBoundsWithInset(padding)
        const removeButtonSize = RHRequiredDocumentsEditorViewTokens.removeButtonSize
        const headerRowFrame = bounds.rectangleWithHeight(
            Math.max(labelHeight, removeButtonSize)
        )
        const nameLabelFrame = headerRowFrame.rectangleWithWidth(
            headerRowFrame.width - removeButtonSize - RHSpacing.small
        )
        
        nameLabelFrame
            .assignedAsFrameOfView(this.nameLabel)

        nameLabelFrame
            .rectangleForNextColumn(RHSpacing.small, removeButtonSize)
            .rectangleWithHeight(removeButtonSize, 0.5)
            .assignedAsFrameOfView(this.removeButton)

        headerRowFrame
            .rectangleForNextRow(RHSpacing.small, fieldHeight)
            .assignedAsFrameOfView(this.nameTextField)
            .rectangleForNextRow(padding * 0.5, 200)
            .assignedAsFrameOfView(this.descriptionEditor)
        
        // Part checkboxes row — only if there are any
        if (this._partCheckboxes.length > 0) {
            const checkboxesStartFrame = this.descriptionEditor.frame
                .IF(this.partSelectionValidationMessageLabel.superview === this)
                .rectangleForNextRow(padding * 0.5, this.partSelectionValidationMessageLabel)
                .assignedAsFrameOfView(this.partSelectionValidationMessageLabel)
                .ENDIF()
                .rectangleForNextRow(padding * 0.5, labelHeight)
            
            // Each checkbox+label pair is laid out as a tight inline group
            const pairs = this._partCheckboxes.map((checkbox, index) => ({
                checkbox,
                label: this._partCheckboxLabels[index]
            }))
            
            let currentX = checkboxesStartFrame.x
            const checkboxSize = labelHeight
            
            pairs.forEach(pair => {
                const pairWidth = Math.max(padding * 6,
                    checkboxSize + padding * 0.5 + pair.label.intrinsicContentWidth(labelHeight))
                pair.checkbox.frame = checkboxesStartFrame
                    .rectangleWithWidth(checkboxSize)
                    .rectangleWithX(currentX)
                pair.label.frame = checkboxesStartFrame
                    .rectangleWithWidth(pairWidth - checkboxSize - padding * 0.25)
                    .rectangleWithX(currentX + checkboxSize + padding * 0.25)
                currentX += pairWidth
            })
        }
    }
    
    
    override intrinsicContentHeight(constrainingWidth?: number): number {
        const padding = this.core.paddingLength
        const labelHeight = padding * 2
        const fieldHeight = RHForms.fieldSingleLineFieldHeight
        // nameLabel + name + description + optional checkboxes row + padding
        const checkboxRowHeight = this._partCheckboxes.length > 0 ? labelHeight + padding * 0.5 : 0
        const validationMessageHeight = IF(this.partSelectionValidationMessageLabel.superview === this)(() =>
            this.partSelectionValidationMessageLabel.intrinsicContentHeight(
                (constrainingWidth ?? this.frame.width) - padding * 2
            ) + padding * 0.5
        ).ELSE(() => 0)
        const headerRowHeight = Math.max(
            labelHeight,
            RHRequiredDocumentsEditorViewTokens.removeButtonSize
        )
        return padding * 2 +
            headerRowHeight +
            RHSpacing.small +
            fieldHeight +
            padding * 0.5 +
            200 +
            checkboxRowHeight +
            validationMessageHeight
    }
    
}


// ============================================================================
// RequiredDocumentsEditorView
// ============================================================================

/**
 * Procurer-side editor that manages the list of required documents for a procurement.
 * Lives in NewProcurementViewController between the cost summary and duration section.
 *
 * Usage:
 *   - Set `parts` whenever the part list changes (add/remove/load).
 *   - Call `requiredDocumentsByPartNumber()` in saveDraft() to get the per-part arrays.
 *   - Call `loadFromParts()` when populating an existing procurement.
 *   - Set `_onRowsChanged` to be notified when rows are added or removed (used by
 *     the parent view controller to rewire keyboard focus).
 */
export class RequiredDocumentsEditorView extends UIView {
    
    readonly sectionTitleLabel = new UITextView(
        undefined,
        UITextView.type.header4
    ).addedAsSubviewToView(this).configuredWithObject({
        text: "Nõutud dokumendid"
    })
    readonly templateLockButton = new ProcurementTemplateLockButton("Nõutud dokumendid")
    
    readonly explanationLabel = new UITextView().addedAsSubviewToView(this).configuredWithObject({
        text: "Lisa dokumendid, mida pakkuja peab pakkumuse esitamisel üles laadima. Saad täpsustada, millistele osadele iga dokument kehtib."
    })

    readonly lockIndicator = new UITextView().configuredWithObject({
        text: "Väli on hankemallis lukustatud",
        textColor: RHColor.textSecondary,
        style: { fontSize: "0.85em" }
    })
    
    readonly rowsContainerView = new UIView().addedAsSubviewToView(this).configuredWithObject({
        layoutSubviews: EXTEND(() => {
            
            // Layout the rows container to stack its children
            this.rowsContainerView.contentBoundsWithInset(0)
                .framesByDistributingViewsAsColumn(this._rows, RHSpacing.small)
        })
    })
    
    readonly addDocumentButton = new RHButton().addedAsSubviewToView(this).configuredWithObject({
        titleLabel: { text: "+ Lisa nõutud dokument" },
        variant: RHButtons.compactPrimaryVariant,
        contentPadding: RHButtons.compactPrimaryContentPadding,
        style: {
            fontSize: RHButtons.compactPrimaryFontSize
        },
        controlEventTargetAccumulator: {
            PrimaryActionTriggered: () => {
                this._addRow()
            }
        }
    })
    
    _rows: RequiredDocumentRowView[] = []
    _parts: Pick<RHProcurementPart, "partNumber" | "_id">[] = []
    _readOnly = NO
    _isDocumentNameRequired = YES
    _readOnlyPartIds: string[] = []

    get readOnlyPartIds(): string[] {
        return this._readOnlyPartIds
    }

    set readOnlyPartIds(readOnlyPartIds: string[]) {
        this._readOnlyPartIds = [...readOnlyPartIds]
        this._applyReadOnlyState()
    }

    _applyReadOnlyState() {
        this.addDocumentButton.enabled = !this._readOnly && this._parts.some(part =>
            !this._readOnlyPartIds.includes(part._id?.toString() ?? "")
        )
        for (const row of this._rows) {
            row.readOnlyPartIds = this._readOnlyPartIds
            row.isDocumentListReadOnly = this._readOnly
        }
    }
    
    /** Called after rows are added or removed. Used by the parent VC to rewire keyboard focus. */
    _onRowsChanged?: () => void

    /** Called after user-editable required-document data changes. */
    documentsDidChange?: () => void
    
    /** Called after a new row is added. Used by the parent VC to focus the new row's name field. */
    _onRowAdded?: (row: {
        removeButton: UIView,
        nameTextField: UIView,
        descriptionEditor: UIView,
        partCheckboxes: UIView[]
    }) => void
    
    
    constructor(elementID?: string) {
        super(elementID)
    }


    set readOnly(value: boolean) {
        this._readOnly = value
        this._applyReadOnlyState()
        if (value) {
            this.lockIndicator.addedAsSubviewToView(this)
        }
        else {
            this.lockIndicator.removeFromSuperview()
        }
        this.setNeedsLayoutUpToRootView()
    }

    get readOnly(): boolean {
        return this._readOnly
    }


    configureTemplateAuthoringLock(lock?: ProcurementTemplateLockControlConfiguration) {
        this._isDocumentNameRequired = !lock || lock.locked
        for (const row of this._rows) {
            row.isNameRequired = this._isDocumentNameRequired
        }
        if (lock) {
            this.templateLockButton.configure(lock)
            this.templateLockButton.addedAsSubviewToView(this)
        }
        else {
            this.templateLockButton.removeFromSuperview()
        }
        this.setNeedsLayoutUpToRootView()
    }
    
    
    /**
     * Update the parts list. Call this every time the procurer adds or removes a part.
     * All existing rows will rebuild their checkboxes to reflect the new part count.
     */
    set parts(parts: Pick<RHProcurementPart, "partNumber" | "_id">[]) {
        this._parts = parts
        this._updatePartTerminology()
        this._rows.forEach(row => row.updatePartCheckboxesForParts(parts))
        this._applyReadOnlyState()
        this.setNeedsLayoutUpToRootView()
    }
    
    get parts(): Pick<RHProcurementPart, "partNumber" | "_id">[] {
        return this._parts
    }
    
    
    /**
     * Returns a map of partNumber → RHRequiredDocument[] for all parts.
     * Call this in saveDraft() and merge the results into each part's data.
     * Unnamed rows are retained so an incomplete draft can be reopened without losing them.
     */
    requiredDocumentsByPartNumber(): Record<number, RHRequiredDocument[]> {
        const result: Record<number, RHRequiredDocument[]> = {}
        
        this._parts.forEach(part => {
            result[part.partNumber] = []
        })
        
        this._rows.forEach(row => {
            const document = row.requiredDocument()
            row.checkedPartNumbers().forEach(partNumber => {
                if (!result[partNumber]) {
                    result[partNumber] = []
                }
                result[partNumber].push(document)
            })
        })
        
        return result
    }


    sessionSnapshot(): RequiredDocumentsEditorSessionSnapshot {
        return {
            rows: this._rows.map(row => {
                const document = row.requiredDocument()
                return {
                    documentID: row.documentID,
                    nameText: row.nameTextField.text,
                    descriptionText: document.description ?? "",
                    descriptionFormat: document.descriptionFormat,
                    checkedPartNumbers: row.checkedPartNumbers()
                }
            })
        }
    }


    restoreSessionSnapshot(snapshot: RequiredDocumentsEditorSessionSnapshot) {
        this._rows.forEach(row => row.removeFromSuperview())
        this._rows = []

        for (const rowSnapshot of snapshot.rows) {
            const row = this._makeRow(rowSnapshot.documentID)
            row.nameTextField.text = rowSnapshot.nameText
            row.loadDescription({
                description: rowSnapshot.descriptionText,
                descriptionFormat: rowSnapshot.descriptionFormat
            })
            row.updatePartCheckboxesForParts(this._parts)
            const checkedPartNumbers = new Set(rowSnapshot.checkedPartNumbers)
            row._partCheckboxes.forEach((checkbox, index) => {
                checkbox.selected = checkedPartNumbers.has(this._parts[index].partNumber)
            })
            row._updatePartSelectionValidationMessage()
        }

        this._applyReadOnlyState()
        this.setNeedsLayoutUpToRootView()
        this._onRowsChanged?.()
    }
    
    
    /**
     * Restores editor state from existing part data when loading a saved procurement.
     * Reads requiredDocuments from the first part that has them, then reconstructs
     * rows and sets checkboxes accordingly.
     */
    loadFromParts(parts: Pick<RHProcurementPart, "partNumber" | "_id" | "requiredDocuments">[]) {
        
        // Remove all existing rows
        this._rows.forEach(row => row.removeFromSuperview())
        this._rows = []
        
        this._parts = parts
        this._updatePartTerminology()
        
        // Collect all unique document IDs across all parts
        const documentMap = new Map<string, { document: RHRequiredDocument, partNumbers: Set<number> }>()
        
        parts.forEach(part => {
            if (!part.requiredDocuments) {
                return
            }
            part.requiredDocuments.forEach(document => {
                if (!documentMap.has(document._id)) {
                    documentMap.set(document._id, {
                        document,
                        partNumbers: new Set()
                    })
                }
                documentMap.get(document._id)!.partNumbers.add(part.partNumber)
            })
        })
        
        // Reconstruct a row for each unique document
        documentMap.forEach(({ document, partNumbers }) => {
            const row = this._makeRow(document._id)
            row.nameTextField.text = document.name
            row.loadDescription(document)
            
            // Build checkboxes first so we can set their state
            row.updatePartCheckboxesForParts(parts)
            
            // Override default (all checked) with the actual per-part selection
            row._partCheckboxes.forEach((checkbox, index) => {
                checkbox.selected = partNumbers.has(parts[index].partNumber)
            })
            row._updatePartSelectionValidationMessage()
        })
        
        this._applyReadOnlyState()
        this.setNeedsLayoutUpToRootView()
        this._onRowsChanged?.()
    }
    
    
    /** Incomplete drafts may omit names, but every row must have a destination part. */
    validateFields(options?: { canOmitDocumentNames?: boolean }): RHValidationIssue[] {
        const validationIssues: RHValidationIssue[] = []
        this._rows.forEach((row, rowIndex) => {
            if (!options?.canOmitDocumentNames && !row.nameTextField.validate()) {
                validationIssues.push({
                    focusTarget: row.nameTextField,
                    message: `Nõutud dokumendi ${rowIndex + 1} nimetus on puudu.`
                })
            }
            row._updatePartSelectionValidationMessage()
            if (row.checkedPartNumbers().length === 0) {
                validationIssues.push({
                    focusTarget: row._partCheckboxes.firstElement ?? row.nameTextField,
                    message: `Nõutud dokument ${rowIndex + 1}: ${row.partSelectionValidationMessageLabel.text}`
                })
            }
        })
        return validationIssues
    }
    
    
    /**
     * Removes all rows. Used by clearAllFields() in the parent VC.
     */
    clear() {
        this._rows.forEach(row => row.removeFromSuperview())
        this._rows = []
        this._updatePartTerminology()
        this.setNeedsLayoutUpToRootView()
        this._onRowsChanged?.()
    }
    
    
    _addRow() {
        if (!this.addDocumentButton.enabled) {
            return
        }
        const row = this._makeRow()
        row.updatePartCheckboxesForParts(this._parts)
        this.setNeedsLayoutUpToRootView()
        this._onRowsChanged?.()
        this.documentsDidChange?.()
        this._onRowAdded?.({
            removeButton: row.removeButton,
            nameTextField: row.nameTextField,
            descriptionEditor: row.descriptionEditor,
            partCheckboxes: row._partCheckboxes
        })
    }
    
    
    _makeRow(documentID?: string): RequiredDocumentRowView {
        const row = new RequiredDocumentRowView(
            `RequiredDocumentRow_${MAKE_ID()}`,
            documentID
        ).addedAsSubviewToView(this.rowsContainerView)
        row.isNameRequired = this._isDocumentNameRequired
        
        // this.rootView.performForcedSubtreeLayout()
        // this.rootView.performForcedSubtreeLayout()
        // this.rootView.performForcedSubtreeLayout()
        
        row.removalWasRequested = () => {
            if (row.isDocumentReadOnly) {
                return
            }
            this._rows.removeElement(row)
            row.removeFromSuperview()
            this.rowsContainerView.setNeedsLayoutUpToRootView()
            this._onRowsChanged?.()
            this.documentsDidChange?.()
        }
        row.rowDidChange = () => {
            this.documentsDidChange?.()
        }
        
        this._rows.push(row)
        row.readOnlyPartIds = this._readOnlyPartIds
        row.isDocumentListReadOnly = this._readOnly
        return row
    }
    
    private _updatePartTerminology() {
        this.explanationLabel.text = RHProcurementPartDisplay.requiredDocumentsExplanationText(this._parts.length)
    }
    

    override layoutSubviews() {
        super.layoutSubviews()
        
        const padding = this.core.paddingLength
        const labelHeight = padding * 2
        const bounds = this.contentBoundsWithInset(0)
        
        const templateLockControlSize = RHNewProcurementViewTokens.templateLockControl.size
        const titleRowFrame = bounds.rectangleWithHeight(Math.max(labelHeight, templateLockControlSize))

        this.templateLockButton.layoutBesideTitleLabelInFrame(this.sectionTitleLabel, titleRowFrame)

        titleRowFrame
            .IF(this.lockIndicator.superview === this)
            .rectangleForNextRow(RHSpacing.tiny, this.lockIndicator)
            .assignedAsFrameOfView(this.lockIndicator)
            .ENDIF()
            
            .rectangleForNextRow(RHProcurementWorkflowTitleTokens.contentTopGap, this.explanationLabel)
            .assignedAsFrameOfView(this.explanationLabel)
            
            .rectangleForNextRow(padding, this.rowsContainerView)
            .assignedAsFrameOfView(this.rowsContainerView)
            
            .rectangleForNextRow(
                this._rows.length > 0
                ? padding
                : RHSpacing.medium,
                RHButtons.compactPrimaryHeight
            )
            .rectangleWithInsets(RHSpacing.medium, RHSpacing.medium, 0, 0)
            .rectangleWithMaxWidth(
                this.addDocumentButton.titleLabel.intrinsicContentWidth(
                    RHButtons.compactPrimaryHeight
                ) + RHButtons.compactPrimaryContentPadding * 2
            )
            .assignedAsFrameOfView(this.addDocumentButton)
    }
    
}
